import React, { useEffect, useState } from "react";
import { Modal, Form, Input, DatePicker, Select, message, Button } from "antd";
import PhoneInput from "react-phone-input-2";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedPatient,
  selectInsuranceLookup,
  selectIsEditPatient,
} from "../PatientsSelectors";
import {
  createOrUpdatePatient,
  fetchPatientList,
  fetchInsuranceCompaniesLookup,
} from "../PatientsThunk";
import { closePatientModal } from "../PatientsSlice";
import dayjs from "dayjs";
import { getISOFromCountryName } from "../../../lib/currency";

const PatientModal = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const selected = useSelector(selectSelectedPatient);
  const isEdit = useSelector(selectIsEditPatient);
  const insuranceLookup = useSelector(selectInsuranceLookup);

  const [phoneInputValue, setPhoneInputValue] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("");

  const authInfo = useSelector((state) => state.auth.auth_info);

  useEffect(() => {
    dispatch(fetchInsuranceCompaniesLookup());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && selected) {
      const phone = selected.phone_no || "";
      const countryCode = selected.country_code
        ? String(selected.country_code)
        : "";
      const combined = countryCode ? `${countryCode}${phone}` : phone;

      form.setFieldsValue({
        patient_name: selected.patient_name || "",
        emirates_id: selected.emirates_id || "",
        co_pay_amount: selected.co_pay_amount || "",
        insurance_company_id: selected.insurance_company_id || "",
        insurance_no: selected.insurance_number || "",
        insurance_validity: selected.insurance_validity
          ? dayjs(selected.insurance_validity)
          : null,
        address: selected.address || "",
        notes: selected.notes || "",
        phone: phone || "",
        country_code: countryCode || "",
      });

      setPhoneDialCode(countryCode);
      setPhoneInputValue(combined || "");
    } else {
      form.resetFields();
      setPhoneDialCode("");
      setPhoneInputValue("");
    }
  }, [selected, isEdit, form]);

  const handleCancel = () => {
    dispatch(closePatientModal());
    form.resetFields();
    setPhoneDialCode("");
    setPhoneInputValue("");
  };

  // ✅ Co-pay percentage validator (0–100%)
  const validateCoPay = (_, value) => {
    if (value === undefined || value === null || value === "") {
      return Promise.reject(new Error("Discount is required"));
    }
    const asStr = String(value).trim();
    // allow integers or decimals with up to 2 decimal places
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(asStr)) {
      return Promise.reject(
        new Error("Enter a valid percentage (up to 2 decimals)")
      );
    }
    const num = Number(asStr);
    if (Number.isNaN(num) || num < 0 || num > 100) {
      return Promise.reject(new Error("Percentage must be between 0 and 100"));
    }
    return Promise.resolve();
  };

  // ✅ Phone validator (7–15 digits)
  const validatePhone = (_, value) => {
    const phone = value ? String(value).replace(/\D/g, "") : "";
    if (!phone) {
      return Promise.reject(new Error("Contact number is required"));
    }
    if (phone.length < 7 || phone.length > 15) {
      return Promise.reject(
        new Error("Enter a valid contact number (7–15 digits)")
      );
    }
    return Promise.resolve();
  };

  // Handle phone input change
  const onPhoneChange = (value, country) => {
    const digits = String(value || "").replace(/\D/g, "");
    const dial = country?.dialCode ? String(country.dialCode) : "";

    let local = digits;
    if (dial && local.startsWith(dial)) {
      local = local.slice(dial.length);
    }

    form.setFieldsValue({
      phone: local,
      country_code: dial,
    });

    setPhoneInputValue(digits);
    setPhoneDialCode(dial);
  };

  const handleFinish = async (values) => {
    const payload = {
      emirates_id: values.emirates_id,
      insurance_company_name_id: values.insurance_company_id,
      insurance_company_selected_name:
        insuranceLookup.find((i) => i.value === values.insurance_company_id)
          ?.label || "",
      co_pay_amount: values.co_pay_amount,
      patient_name: values.patient_name,
      phone_no: values.phone ? String(values.phone).replace(/\D/g, "") : "",
      country_code: values.country_code || "",
      insurance_no: values.insurance_no,
      insurance_validity: values.insurance_validity
        ? values.insurance_validity.toISOString()
        : "",
      address: values.address,
      notes: values.notes,
      patient_action: isEdit ? "update" : "create",
      patient_id: isEdit ? selected.patient_id : undefined,
    };

    try {
      await dispatch(createOrUpdatePatient(payload)).unwrap();
      message.success("Patient saved successfully");
      dispatch(closePatientModal());
      dispatch(fetchPatientList());
    } catch (err) {
      message.error(err?.message || "Save failed");
    }
  };

  const defaultCountry = getISOFromCountryName(authInfo?.country);

  return (
    <Modal
      title={isEdit ? "Update Patient" : "New Patient"}
      visible={true}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          label="Patient Name"
          name="patient_name"
          data-testid="patient_name"
          rules={[{ required: true, message: "Patient Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Emirates Id"
          name="emirates_id"
          data-testid="emirates_id"
          rules={[{ required: true, message: "Emirates ID is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Co-Pay %"
          name="co_pay_amount"
          data-testid="co_pay_amount"
          rules={[{ validator: validateCoPay }]}
        >
          <Input placeholder="Enter Co-pay percentage" />
        </Form.Item>

        <Form.Item
          label="Insurance Company"
          name="insurance_company_id"
          rules={[{ required: true, message: "Insurance is required" }]}
        >
          <Select
            showSearch
            options={insuranceLookup}
            placeholder="Select Insurance"
            data-testid="patient_insurance"
            optionFilterProp="label"
            filterOption={(input, option) =>
              option?.label?.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {/* ✅ Contact Number (same pattern as CompanyModal) */}
        <Form.Item label="Contact No" required>
          <PhoneInput
            country={defaultCountry}
            value={phoneInputValue}
            data-testid="patient_contact_no"
            onChange={onPhoneChange}
            inputStyle={{
              width: "100%",
              height: "39.6px",
              background: "rgba(0,0,0,0.04)",
              borderColor: "transparent",
            }}
            placeholder="Contact number"
          />
          <Form.Item
            name="phone"
            noStyle
            rules={[{ validator: validatePhone }]}
          >
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="country_code" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form.Item>

        <Form.Item
          label="Insurance Number"
          data-testid="insurance_number"
          name="insurance_no"
          rules={[{ required: true, message: "Insurance Number is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Insurance Validity"
          name="insurance_validity"
          rules={[
            { required: true, message: "Insurance Validity is required" },
          ]}
        >
          <DatePicker style={{ width: "100%" }} data-testid="insurance_validity" />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
          data-testid="patient_address"
          rules={[{ required: true, message: "Address is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Notes" data-testid="patient_notes" name="notes">
          <Input />
        </Form.Item>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            marginTop: "1.5rem",
          }}
        >
          <Button type="button" onClick={handleCancel}>
            Discard
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            data-testid="patient_save"
          >
            {isEdit ? "Update" : "Add Patient"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default PatientModal;
